import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Building2, ChevronRight, Wrench, ShieldCheck, Clock, Zap } from "lucide-react";

/* ============ Select Manufacturing Unit — premium glass redesign ============
   Presentation only: same /units fetch, same auto-select when the user has a
   single unit, same selectUnit() + navigate flow, same dynamic data.
   One consistent blue identity for every card (per user request — no
   multicolour cycling). */
const UNIT_ACCENTS = [
  { chip: "bg-blue-100/80 dark:bg-blue-950/50", icon: "text-blue-600 dark:text-blue-400", code: "text-blue-600 dark:text-blue-400", arrow: "bg-blue-100/80 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400" },
];
export default function UnitSelector() {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [iconPop, setIconPop] = useState(false);   // click → pop-out animation
  const { selectUnit, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Deep-link return target (e.g. a scanned /trace/{token}) threaded from login.
  const from = location.state?.from;
  const afterSelect = () =>
    (from && from.startsWith("/") && !from.startsWith("//") && from !== "/login") ? from : "/dashboard";

  const fetchUnits = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const response = await api.get("/units");
      setUnits(response.data);
      if (response.data.length === 1) {
        selectUnit(response.data[0]);
        navigate(afterSelect());
      }
    } catch (error) {
      console.error("Error fetching units:", error);
      setFailed(true);
      toast.error("Failed to load units");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchUnits(); }, [fetchUnits]);

  const handleSelectUnit = (unit) => {
    selectUnit(unit);
    navigate(afterSelect());
  };

  return (
    <div className="min-h-screen usel-bg flex items-center justify-center p-6 relative overflow-hidden">
      {/* two near-invisible ambient glows (spec §22) + reference corner décor */}
      <div className="usel-blob usel-blob-a" aria-hidden="true" />
      <div className="usel-blob usel-blob-b" aria-hidden="true" />
      <div className="usel-glow usel-glow-a" aria-hidden="true" />
      <div className="usel-glow usel-glow-b" aria-hidden="true" />
      <div className="usel-dots usel-dots-white" style={{ top: "7%", left: "3%" }} aria-hidden="true" />
      <div className="usel-dots" style={{ bottom: "10%", right: "3%" }} aria-hidden="true" />
      <div className="usel-rings" aria-hidden="true" />

      <div className="w-full max-w-[600px] relative -mt-8">
        {/* top icon → title → subtitle, entering in sequence */}
        <div className="text-center mb-8">
          <button type="button" aria-label="AAMPL MES"
                  onMouseEnter={() => { setIconPop(false); requestAnimationFrame(() => setIconPop(true)); }}
                  onAnimationEnd={() => setIconPop(false)}
                  className={`usel-in usel-icon w-[72px] h-[72px] rounded-[22px] mx-auto mb-5 flex items-center justify-center
                              cursor-pointer select-none focus:outline-none ${iconPop ? "usel-icon-pop" : ""}`}>
            <div className="usel-icon-inner w-[52px] h-[52px] rounded-2xl flex items-center justify-center">
              <Wrench className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </button>
          <h1 className="usel-in text-[28px] font-bold tracking-[-0.4px] text-slate-900 dark:text-slate-100"
              style={{ animationDelay: "90ms" }}>
            Select Manufacturing Unit
          </h1>
          <p className="usel-in text-[14px] text-slate-500 dark:text-slate-400 mt-2"
             style={{ animationDelay: "160ms" }}>
            Welcome back, <span className="text-blue-700 dark:text-blue-400 font-medium">{user?.name}</span>. Choose a unit to continue.
          </p>
        </div>

        {loading ? (
          <div className="space-y-4" data-testid="unitsel-skeleton">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="usel-card h-[88px] rounded-2xl flex items-center px-5 gap-4">
                <div className="w-11 h-11 rounded-xl aampl-skeleton-line !m-0" />
                <div className="flex-1 space-y-2">
                  <div className="aampl-skeleton-line" style={{ width: "34%", height: 12 }} />
                  <div className="aampl-skeleton-line" style={{ width: "22%", height: 9 }} />
                </div>
              </div>
            ))}
          </div>
        ) : failed ? (
          <div className="usel-card usel-in rounded-2xl p-8 text-center" data-testid="unitsel-error">
            <p className="text-sm text-red-600 mb-4">Unable to load manufacturing units.</p>
            <button type="button" onClick={fetchUnits}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
                    style={{ background: "linear-gradient(135deg, #1749D6, #2563EB)" }}>
              Retry
            </button>
          </div>
        ) : units.length === 0 ? (
          <div className="usel-card usel-in rounded-2xl p-10 text-center">
            <Building2 className="w-10 h-10 mx-auto text-slate-400 mb-3" />
            <p className="text-sm text-slate-500">No units available. Please contact your administrator.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {units.map((unit, i) => {
              const a = UNIT_ACCENTS[i % UNIT_ACCENTS.length];
              return (
              <button
                key={unit.id}
                type="button"
                onClick={() => handleSelectUnit(unit)}
                className={`usel-in usel-card group w-full h-[88px] rounded-2xl px-5 flex items-center justify-between text-left
                            transition-all duration-200 ease-out hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm
                            focus:outline-none focus-visible:ring-[3px] focus-visible:ring-blue-600/15 focus-visible:border-blue-500/40
                            ${i === 0 ? "usel-card-primary" : ""}`}
                style={{ animationDelay: `${240 + i * 50}ms` }}
                data-testid={`unit-${unit.code}`}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className={`usel-unit-chip w-12 h-12 shrink-0 rounded-xl ${a.chip} flex items-center justify-center`}>
                    <Building2 className={`w-5 h-5 ${a.icon}`} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-[16px] font-semibold text-slate-900 dark:text-slate-100 truncate">{unit.name}</h3>
                    <p className="text-[12.5px] text-slate-500 dark:text-slate-400 mt-0.5 truncate">
                      Code: <span className={`font-semibold ${a.code}`}>{unit.code}</span>
                      {unit.address && <span> • {unit.address}</span>}
                    </p>
                  </div>
                </div>
                <div className={`w-9 h-9 shrink-0 rounded-full ${a.arrow} flex items-center justify-center transition-transform duration-200 group-hover:translate-x-[3px]`}>
                  <ChevronRight className="w-[18px] h-[18px]" />
                </div>
              </button>
              );
            })}
          </div>
        )}

        {/* quiet reassurance strip, as in the reference */}
        {!loading && !failed && units.length > 0 && (
          <div className="usel-in flex items-center justify-center gap-8 mt-10 text-slate-500 dark:text-slate-400"
               style={{ animationDelay: `${240 + units.length * 50 + 80}ms` }}>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-blue-600" />
              <div className="text-left">
                <p className="text-[11px] font-semibold text-slate-700 dark:text-slate-300 leading-tight">Secure Access</p>
                <p className="text-[10px] leading-tight">Role based authentication</p>
              </div>
            </div>
            <div className="w-px h-6 bg-slate-200 dark:bg-slate-700" />
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-600" />
              <div className="text-left">
                <p className="text-[11px] font-semibold text-slate-700 dark:text-slate-300 leading-tight">Real-time Sync</p>
                <p className="text-[10px] leading-tight">Live data across units</p>
              </div>
            </div>
            <div className="w-px h-6 bg-slate-200 dark:bg-slate-700" />
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-blue-600" />
              <div className="text-left">
                <p className="text-[11px] font-semibold text-slate-700 dark:text-slate-300 leading-tight">High Performance</p>
                <p className="text-[10px] leading-tight">Optimized for speed</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
